import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { money, SUPPLIER_DOC_TYPES, SUPPLIER_DOC_STATUSES, STATUS_COLORS } from "@/lib/constants";
import { Search, Filter, FileText, Plus, FileCode2, FileType } from "lucide-react";
import { DocumentoProveedorDialog } from "@/components/supplier-documents/DocumentoProveedorDialog";

function DocTypeBadge({ value }) {
  const t = SUPPLIER_DOC_TYPES.find((x) => x.value === value);
  const tone = value === "factura" ? "blue"
    : value === "nota_credito" ? "amber"
    : "slate";
  return <Badge className={STATUS_COLORS[tone]}>{t?.label || value}</Badge>;
}

function DocStatusBadge({ value }) {
  const s = SUPPLIER_DOC_STATUSES.find((x) => x.value === value);
  return <Badge className={STATUS_COLORS[s?.tone || "slate"]}>{s?.label || value}</Badge>;
}

export default function DocumentosProveedores() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const navigate = useNavigate();
  const canQuery = !isSuperAdmin || !!activeTenantId;
  const canCreate = hasPermission("supplier_documents:create");

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("activo");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);

  const tenantParam = useMemo(
    () => (isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {}),
    [isSuperAdmin, activeTenantId],
  );

  async function fetchList() {
    if (!canQuery) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = {
        ...tenantParam,
        search: search || undefined,
        doc_type: filterType !== "all" ? filterType : undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        supplier_id: filterSupplier !== "all" ? filterSupplier : undefined,
        date_from: filterDateFrom || undefined,
        date_to: filterDateTo || undefined,
      };
      const [{ data }, { data: sup }] = await Promise.all([
        api.get("/supplier-documents", { params }),
        api.get("/suppliers", { params: { ...tenantParam, include_inactive: true } }),
      ]);
      setItems(data);
      setSuppliers(sup);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search, filterType, filterStatus, filterSupplier,
    filterDateFrom, filterDateTo, activeTenantId, isSuperAdmin,
  ]);

  return (
    <div className="space-y-6" data-testid="page-supplier-documents">
      <PageHeader
        title="Documentos de proveedores"
        description="Módulo administrativo. Sube PDF/XML de facturas y notas de crédito, y ligalos a OC, CxP o devoluciones. No sustituye contabilidad ni timbrado fiscal."
        actions={
          canCreate ? (
            <Button
              onClick={() => setDlgOpen(true)}
              data-testid="btn-new-supplier-document"
              className="btn-primary-tenant"
            >
              <Plus className="h-4 w-4 mr-2" /> Nuevo documento
            </Button>
          ) : null
        }
      />

      <div className="bg-white border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar folio / UUID / notas…"
              className="pl-9 h-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="sd-search"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="sd-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {SUPPLIER_DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="sd-filter-status"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              {SUPPLIER_DOC_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSupplier} onValueChange={setFilterSupplier}>
            <SelectTrigger data-testid="sd-filter-supplier"><SelectValue placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.trade_name || s.legal_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} data-testid="sd-filter-date-from" />
            <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} data-testid="sd-filter-date-to" />
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="sd-count">
        {items.length} {items.length === 1 ? "documento" : "documentos"}
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Sin documentos"
            description={canCreate ? "Sube el primer documento con el botón 'Nuevo documento'." : "Aún no hay documentos registrados para los filtros actuales."}
            icon={FileText}
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Folio ext.</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Vínculo</TableHead>
                <TableHead>Archivos</TableHead>
                <TableHead>Estatus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow
                  key={d.id}
                  className="tbl-row cursor-pointer"
                  onClick={() => navigate(`/documentos-proveedores/${d.id}`)}
                  data-testid={`row-sd-${d.id}`}
                >
                  <TableCell><DocTypeBadge value={d.doc_type} /></TableCell>
                  <TableCell className="mono text-xs">
                    {[d.external_serie, d.external_folio].filter(Boolean).join("-") || "—"}
                  </TableCell>
                  <TableCell className="text-sm">{d.issue_date || "—"}</TableCell>
                  <TableCell className="text-sm">{d.supplier_name || "—"}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(d.total)}</TableCell>
                  <TableCell className="text-xs">
                    {d.purchase_order_folio && <div><span className="text-muted-foreground">OC:</span> <span className="mono">{d.purchase_order_folio}</span></div>}
                    {d.purchase_return_folio && <div><span className="text-muted-foreground">DEV:</span> <span className="mono">{d.purchase_return_folio}</span></div>}
                    {d.accounts_payable_status && <div><span className="text-muted-foreground">CxP:</span> {d.accounts_payable_status}</div>}
                    {!d.purchase_order_folio && !d.purchase_return_folio && !d.accounts_payable_status && <span>—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-2">
                      {d.pdf_url && <FileType className="h-4 w-4 text-red-600" title="PDF adjunto" />}
                      {d.xml_url && <FileCode2 className="h-4 w-4 text-blue-600" title="XML adjunto" />}
                    </div>
                  </TableCell>
                  <TableCell><DocStatusBadge value={d.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DocumentoProveedorDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        onSaved={() => { setDlgOpen(false); fetchList(); }}
        suppliers={suppliers}
        tenantParam={tenantParam}
      />
    </div>
  );
}
